import { Panel } from "../../../components/panels/Panel";
import { Fragment, useState, useMemo, useEffect } from "react";
import { ArchiveBoxIcon, ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { InboxIcon } from '@heroicons/react/24/outline';
import { fetchData, patchData, useGetData } from "../../../services/api/api-tools";
import { Loader } from "../../../components/loading/Loader";
import { formatSecondsToHoursMinutesSeconds } from "../../../helpers/date/date-helper";
import { Thumbnail } from "./Thumbnail";
import { VideoDetailsModal } from "./VideoDetailsModal";
import { InputMultiSelectCheckbox } from "../../../components/forms/inputs/InputMultiSelectCheckbox";
import { CONSTRUCTION_EVENT_TYPES } from "../../../services/gallery/gallery-service";
import ToggleSwitch from "../../../components/toggle/ToggleSwitch";
import { HeartIcon } from "@heroicons/react/20/solid";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { ConfirmModal } from "../../../components/modals/ConfirmModal";
import { BatteryIndicator } from "../../../components/others/BatteryIndicator";

export const EventScoresGalleryPage = ({ showFilters=false, showUsedSpaceMode='disk', showScores=false, shortsMode=false }) => {

  const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const formatMonthLabel = (monthKey) => {
    // monthKey = "07/2025"
    const [month, year] = monthKey.split("/").map(Number);
    return `${MONTHS[month - 1]} ${year}`;
  };

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedEventTypes, setSelectedEventTypes] = useState([CONSTRUCTION_EVENT_TYPES[0]]);
  const [viewEssential, setViewEssential] = useState(false);
  const [clipSearch, setClipSearch] = useState('');

  const expirationTimestamp = Number(process.env.REACT_APP_EXPIRATION_CLIENT_DATE);
  const nowTimestamp = Math.floor(Date.now() / 1000);
  const remainingDays = Math.ceil((expirationTimestamp - nowTimestamp) / (24 * 60 * 60));
  const isLicenseExpired = remainingDays <= 0;
  const shouldShowLicenseWarning = remainingDays <= 30;
  const [isLicensePopupOpen, setIsLicensePopupOpen] = useState(shouldShowLicenseWarning);
  const expirationDate = new Date(expirationTimestamp * 1000).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const licenseMessage = isLicenseExpired
    ? `Votre licence a expiré le ${expirationDate}. Merci de renouveller votre licence pour accéder de nouveau à l'application.`
    : `Votre licence arrive à expiration le ${expirationDate}. Merci de la renouveler rapidement. L'application ne sera plus disponible à partir de cette date.`;

  const [hoveredSnapshotId, setHoveredSnapshotId] = useState(null);
  const [selectedSnapshotIds, setSelectedSnapshotIds] = useState(() => new Set());
  const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState(false);

  const records = useGetData('records', 'records');

  const availableEventTypes = useMemo(() => {
    if (!records?.data) return [CONSTRUCTION_EVENT_TYPES[0]];

    const eventNames = new Set(records.data.map((record) => record.data?.event_name).filter(Boolean));
    const knownEventTypes = CONSTRUCTION_EVENT_TYPES.slice(1).filter((eventType) => eventNames.has(eventType.name));
    const knownEventNames = new Set(knownEventTypes.map((eventType) => eventType.name));
    const unknownEventTypes = [...eventNames].filter((eventName) => !knownEventNames.has(eventName))
      .map((eventName, index) => ({
        id: CONSTRUCTION_EVENT_TYPES.length + index,
        name: eventName,
        display_text: eventName,
        tag_text: eventName,
        threshold: null,
        criteria: []
      }));

    return [CONSTRUCTION_EVENT_TYPES[0], ...knownEventTypes, ...unknownEventTypes];
  }, [records?.data]);

  const [mediaStorage, setMediaStorage] = useState(null);
  useEffect(() => {
    (async () => {
      const mediaStorage = await fetchData('media_storage', null, 'POST', { 'disk_total_bytes': 500 * 1024 * 1024 * 1024 });
      setMediaStorage(mediaStorage);
    })();
  }, []);

  // Grouped by months
  const [openMonths, setOpenMonths] = useState({}); // { "12/2025": true/false }
  const toggleMonth = (monthKey) => {
    setOpenMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  // Sort records based on scores and dates
  const selectedNames = new Set(selectedEventTypes.map((t) => t.name));
  const isAll = selectedNames.has('all') || selectedEventTypes.length === 0;

  const clipQuery = clipSearch.trim();
  const hasClipQuery = clipQuery.length > 0;

  const typeRecords = records?.data ? records.data.filter((record) => {
    const matchesArchived = !record.is_archived;
    const matchesType = isAll || selectedNames.has(record.data?.event_name);
    const matchesId = !hasClipQuery || String(record.id).includes(clipQuery);
    return matchesArchived && matchesType && matchesId;
  }) : [];
  const sortedRecords = typeRecords.sort((a, b) => b.date - a.date);

  const dataByMonth = useMemo(() => {
    // 1) Group by month -> day
    const grouped = sortedRecords.reduce((acc, record) => {
      const d = new Date(record.date * 1000);

      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const monthKey = `${month}/${year}`; // "12/2025"

      const day = String(d.getDate()).padStart(2, "0");
      const dayKey = `${day}/${month}/${year}`; // "22/12/2025"

      if (!acc[monthKey]) acc[monthKey] = {};
      if (!acc[monthKey][dayKey]) acc[monthKey][dayKey] = [];
      acc[monthKey][dayKey].push(record);

      return acc;
    }, {});

    // 2) Apply "viewEssential" inside each day
    const monthEntries = Object.entries(grouped)
      // tri des mois décroissant (année puis mois)
      .sort(([a], [b]) => {
        const [ma, ya] = a.split("/").map(Number);
        const [mb, yb] = b.split("/").map(Number);
        if (ya !== yb) return yb - ya;
        return mb - ma;
      })
      .map(([monthKey, daysObj]) => {
        const sortedDaysEntries = Object.entries(daysObj)
          // tri des jours décroissant (date)
          .sort(([dayA], [dayB]) => {
            const [da, ma, ya] = dayA.split("/").map(Number);
            const [db, mb, yb] = dayB.split("/").map(Number);
            return new Date(yb, mb - 1, db) - new Date(ya, ma - 1, da);
          })
          .map(([dayKey, dayRecords]) => {
            let filtered;

            if (viewEssential) {
              const favorites = dayRecords.filter((r) => r.is_favorite);
              // Uncomment to add top scores records (alongside favorite ones)
              // const topByScore = dayRecords
              //   .filter((r) => !r.is_favorite)
              //   .sort((a, b) => (b.data?.max_value ?? 0) - (a.data?.max_value ?? 0))
              //   .slice(0, 4);
              //
              // const merged = [...favorites, ...topByScore];
              // const uniqueById = Array.from(new Map(merged.map((r) => [r.id, r])).values());
              const uniqueById = Array.from(new Map(favorites.map((r) => [r.id, r])).values());

              filtered = uniqueById.sort((a, b) => b.date - a.date);
            } else {
              filtered = dayRecords.sort((a, b) => b.date - a.date);
            }

            return [dayKey, filtered];
          });

        return [monthKey, Object.fromEntries(sortedDaysEntries)];
      });

    return Object.fromEntries(monthEntries);
  }, [sortedRecords, viewEssential]);

  const usedSpacePercent = useMemo(() => {
    if (showUsedSpaceMode === 'disk') {
      if (mediaStorage === null) return null;
      else return Math.ceil(mediaStorage.used_percent * 10) / 10;
    }
    else {
      if (records.isLoading) return null;
      const MAX_VIDEOS_NUMBER = 9000;
      return Math.ceil(records?.data?.length * 10 / MAX_VIDEOS_NUMBER * 100) / 10;
    }
  }, [mediaStorage, records]);

  if (records.isLoading || records.isError) return <Loader/>;

  // Manage single/multi video(s) and thumbnail(s)
  const getSrcs = () => {
    // Recorder gallery
    const camsIds = selectedRecord?.data?.cams_ids;
    if (Array.isArray(camsIds) && camsIds.length > 0)
      return camsIds.map((camId) => process.env.REACT_APP_ADDRESS + `public/media/records/${selectedRecord?.id}_${camId}.mp4`);

    // Shorts gallery
    if (shortsMode)
      return process.env.REACT_APP_ADDRESS + `public/media/shorts/${selectedRecord?.id}.mp4`;

    // Regular gallery
    return process.env.REACT_APP_ADDRESS + `public/media/records/${selectedRecord?.id}.mp4`;
  }

  const handleArchiveSelectionClick = async () => {
    const ids = Array.from(selectedSnapshotIds);
    if (ids.length === 0) return;

    for (const id of ids) {
      try {
        const path = `records/${id}`;
        const body = { is_archived: true };
        await patchData(path, body);
      } catch (err) {
        console.error("Archive selection failed for record:", id, err);
      }
    }

    records.refetch();
    setSelectedSnapshotIds(new Set());
  };

  return (
    <>
      <VideoDetailsModal
        selectedRecord={selectedRecord}
        src={getSrcs()}
        isOpen={selectedRecord !== null}
        setIsOpen={() => setSelectedRecord(null)}
        refetchRecords={records.refetch}
        showScores={showScores}
      />

      <ConfirmModal onConfirm={handleArchiveSelectionClick} isOpen={isConfirmArchiveOpen} setIsOpen={setIsConfirmArchiveOpen}
        title="Confirmer l'archivage" text="Êtes-vous sûr de vouloir archiver les clips sélectionnés ?" />

      {(isLicensePopupOpen || isLicenseExpired) &&
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-lg rounded-lg bg-perception-black-800 p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-yellow-300 mb-3">
              {isLicenseExpired ? 'Licence expirée' : 'Renouvellement de licence requis'}
            </h2>
            <p className="text-sm text-white mb-6">{licenseMessage}</p>
            {!isLicenseExpired &&
              <div className="flex justify-end">
                <ActionButton text="J'ai compris" size="sm" onClick={() => setIsLicensePopupOpen(false)} />
              </div>
            }
          </div>
        </div>
      }

      <div className='flex h-full'>
        <Panel category='main' title='GALERIE' noPadding={true} className="flex flex-column">
          {shouldShowLicenseWarning &&
            <div className="bg-yellow-400 text-black text-sm font-semibold px-4 py-3 text-center">
              {licenseMessage}
            </div>
          }

          <div className="flex flex-col xl:flex-row xl:items-center bg-perception-black-800">
            <div className="p-4 w-full xl:w-auto xl:flex-1 flex flex-wrap md:flex-nowrap gap-4 items-center justify-center xl:justify-start">
              <div className="relative w-full sm:w-48 shrink-0">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-perception-gray-300" />
                <input
                  type="text"
                  value={clipSearch}
                  onChange={(e) => setClipSearch(e.target.value)}
                  placeholder="Rechercher un clip (#id)…"
                  className="w-full rounded-md bg-perception-gray-800 text-white text-xs px-7 py-2 outline-none focus:ring-1 focus:ring-perception-blue"
                />
              </div>
              {
                showFilters &&
                <div className="min-w-0">
                  <InputMultiSelectCheckbox
                    items={availableEventTypes}
                    selectedItems={selectedEventTypes}
                    onChange={(items) => setSelectedEventTypes(items)}
                    itemToString={(item) => item?.tag_text}
                  />
                </div>
              }
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span>Voir mes favoris</span>
                <ToggleSwitch enabled={viewEssential} setEnabled={setViewEssential} storageToggleId="view_essential"/>
              </div>
            </div>

            <div className="px-4 py-4 w-full xl:w-auto flex flex-wrap items-center justify-center xl:justify-end gap-4 border-t border-perception-black-900 xl:border-t-0 xl:shrink-0">
              <div className="text-xs flex flex-col items-center justify-center gap-1">
                <span>Espace occupé : {usedSpacePercent}%</span>
                <div className="bg-perception-gray-800 rounded-full w-32 h-2 overflow-hidden">
                  <div className="bg-perception-blue h-full rounded-full" style={{ width: usedSpacePercent + '%'}}/>
                </div>
              </div>
              {showScores && <BatteryIndicator devicesNames={[process.env.REACT_APP_CUSTOMER_NAME + '_1']}/>}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto relative">
            {
              selectedSnapshotIds.size > 0 &&
              <div className="absolute top-4 right-4">
                <ActionButton text="Archiver la sélection" Icon={ArchiveBoxIcon} size="sm" onClick={() => setIsConfirmArchiveOpen(true)}/>
              </div>
            }
            {
              Object.entries(dataByMonth).filter(([, daysObj]) => Object.values(daysObj).some((dayEvents) => Array.isArray(dayEvents) && dayEvents.length > 0))
                .map(([monthKey, daysObj]) => {

                const isOpen = openMonths[monthKey] === true; // closed by default

                return (
                  <Fragment key={monthKey}>
                    {/* Header mois (cliquable) */}
                    <button
                      type="button"
                      onClick={() => toggleMonth(monthKey)}
                      className="w-full flex items-center gap-2 px-4 py-2 mt-2 text-left"
                    >
                      <span className={`rounded-md ${isOpen ? 'bg-perception-blue' : 'bg-perception-gray-800' } px-2 py-1 text-xs font-medium text-white inline-flex items-center gap-2 w-36`}>
                        {isOpen ?
                          <ChevronDownIcon className="h-4 w-4" /> :
                          <ChevronRightIcon className="h-4 w-4" />
                        }
                        {formatMonthLabel(monthKey)}
                      </span>
                      <span className="text-xs text-perception-gray-300">
                        ({Object.values(daysObj).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)} vidéos)
                      </span>
                    </button>

                    {/* Contenu mois */}
                    {isOpen && (
                      <div className="pt-2">
                        {Object.entries(daysObj).map(([date, snapshots]) => (snapshots.length > 0 &&
                          <Fragment key={date}>
                            <span className="rounded-md bg-perception-gray-800 px-2 py-1 text-xs font-medium text-white inline-block ml-4">
                              {date}
                            </span>

                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-4 w-full overflow-y-auto p-4">
                              {snapshots.map((snapshot) => {
                                const camsIds = snapshot?.data?.cams_ids;
                                const thumbnailSrc = Array.isArray(camsIds) && camsIds.length > 0 ?
                                  `${process.env.REACT_APP_ADDRESS}public/media/records/thumbnails/${snapshot.id}_${camsIds[0]}.jpg` :
                                  `${process.env.REACT_APP_ADDRESS}public/media/records/thumbnails/${snapshot.id}.jpg`;
                                const checkboxClass = hoveredSnapshotId === snapshot.id || selectedSnapshotIds.has(snapshot.id) ? 'opacity-100' : 'opacity-0';

                                return (
                                  <Fragment key={snapshot.id}>
                                    <div
                                      className={`h-[200px] w-full aspect-video bg-perception-black-800 bg-cover bg-center rounded-md transition duration-300 text-xs flex justify-end items-end relative overflow-hidden border-2 ${
                                        selectedSnapshotIds.has(snapshot.id) ? 'border-perception-blue' : 'border-transparent'
                                        } ${snapshot?.data?.video_encoding ? '' : 'cursor-pointer hover:transform hover:scale-105'}`}
                                      onClick={() => !(snapshot?.data?.video_encoding) && setSelectedRecord(snapshot)}
                                      onMouseEnter={() => setHoveredSnapshotId(snapshot.id)}
                                      onMouseLeave={() => setHoveredSnapshotId(null)}
                                    >
                                      <Thumbnail src={thumbnailSrc} snapshotData={snapshot.data} />

                                      {/* Selection checkbox */}
                                      {
                                        !(snapshot?.data?.video_encoding) &&
                                          <div className={`absolute bottom-4 right-4 flex gap-2 items-center ${checkboxClass} transition`}
                                               onClick={(e) => e.stopPropagation()}>
                                            <label htmlFor={`chk-${snapshot.id}`}>Sélectionner</label>
                                            <div className="size-5 rounded-full overflow-hidden">
                                              <input
                                                type="checkbox"
                                                checked={selectedSnapshotIds.has(snapshot.id)}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedSnapshotIds((prev) => {
                                                    const next = new Set(prev);
                                                    if (e.target.checked) next.add(snapshot.id);
                                                    else next.delete(snapshot.id);
                                                    return next;
                                                  });
                                                }}
                                                className="size-5"
                                                id={`chk-${snapshot.id}`}
                                              />
                                            </div>
                                          </div>
                                      }

                                      <span className="absolute top-4 left-4 rounded-md backdrop-blur-md bg-white/20 px-2 py-1 text-xs font-medium text-white inline-block">
                                      #{snapshot.id}
                                    </span>
                                      <span className="absolute top-4 right-4 rounded-md backdrop-blur-md bg-white/20 px-2 py-1 text-xs font-medium text-white inline-block ml-4">
                                      {formatSecondsToHoursMinutesSeconds(snapshot.date)}
                                    </span>
                                      {snapshot.is_favorite &&
                                        <span className="absolute bottom-4 left-4">
                                        <HeartIcon className="w-5 h-5 text-perception-blue" />
                                      </span>
                                      }
                                      {
                                        showFilters &&
                                          <span className="absolute bottom-4 right-4 rounded-md backdrop-blur-md bg-white/20 px-2 py-1 text-xs font-medium text-white inline-block ml-4">
                                            { CONSTRUCTION_EVENT_TYPES.find((event_type) => event_type.name === snapshot.data?.event_name)?.tag_text || "AUTRES" }
                                          </span>
                                      }
                                    </div>
                                  </Fragment>
                                )}
                              )}
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    )}
                  </Fragment>
                );
              })
            }
            {
              Object.entries(dataByMonth).length === 0 &&
                <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                  <InboxIcon className="size-24 text-perception-gray-700" />
                  <div className="text-perception-gray-700 text-xl">Aucun événement relevé</div>
                </div>
            }
          </div>
        </Panel>
      </div>
    </>
  );
};